import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import { isEmpty } from 'lodash';
import './SpecialAnnouncementBanner.scss';
import { sessionGetItem } from '../../../utils/storage';
import { USER_ID_KEY } from '../../../global/constants';

// Helper functions outside the component to reduce complexity
const getCheckboxValue = (element, key) => element[key]?.value || false;

const checkAllFalse = elements => {
  const checkboxes = ['ecomm', 'eproc', 'microsite', 'ecommCart', 'ecommCheckout', 'eprocCart'];
  return checkboxes.every(key => !getCheckboxValue(elements, key));
};

const isOnlyCheckboxTrue = (elements, targetKey) => {
  const checkboxes = ['ecomm', 'eproc', 'microsite', 'ecommCart', 'ecommCheckout', 'eprocCart'];
  return checkboxes.every(key =>
    key === targetKey ? getCheckboxValue(elements, key) : !getCheckboxValue(elements, key)
  );
};

// Updated function to handle different site types with improved path matching

const isPathIncluded = includedPathsValue => {
  if (!includedPathsValue) return false;

  const currentPath = window.location.pathname;

  // For other sites (eCommerce, microSite), use the improved path matching

  const includedPaths = includedPathsValue.split(',');

  return includedPaths.some(path => {
    const trimmedPath = path.trim();

    if (trimmedPath.includes('*')) {
      let corePath = trimmedPath.replace(/\*/g, '');

      if (corePath.endsWith('/')) {
        corePath = corePath.slice(0, -1);
      }

      return currentPath.includes(corePath);
    }

    // For exact paths, check if the current path contains or ends with the specified path

    const containsPath = currentPath.includes(trimmedPath);

    const endsWithPath = currentPath.endsWith(trimmedPath);

    return containsPath || endsWithPath;
  });
};

const SpecialAnnouncementBanner = ({ announcementData, siteConfig }) => {
  const [showBanner, setShowBanner] = useState(false);
  const currentPath = window.location.pathname;
  const elements = announcementData?.entities?.[0]?.properties?.elements || {};
  const descriptionValue = elements.description?.value || '';
  const iconValue = elements.icon?.value || '';
  const includedPaths = elements.includedPaths?.value || '';
  const userDetails = sessionGetItem(USER_ID_KEY);
  // Check if current page is cart or checkout
  const isCartPage = currentPath.includes('/cart');
  const isCheckoutPage = currentPath.includes('/checkout');
  const isQuickOrderPage = currentPath.includes('/quick-order');

  // Handle eCommerce all false case
  const handleEcommerceAllFalse = () => isCartPage || isCheckoutPage;

  // Handle eCommerce standard checks
  const handleEcommerceStandard = () => {
    if (isCartPage && getCheckboxValue(elements, 'ecommCart')) return true;
    if (isCheckoutPage && getCheckboxValue(elements, 'ecommCheckout')) return true;
    if (!isCartPage && !isCheckoutPage && getCheckboxValue(elements, 'ecomm')) return true;
    return false;
  };

  // Handle eCommerce site type logic
  const handleEcommerceSite = () => {
    if (checkAllFalse(elements)) {
      return handleEcommerceAllFalse();
    }

    if (isOnlyCheckboxTrue(elements, 'ecommCart')) {
      return isCartPage;
    }

    if (isOnlyCheckboxTrue(elements, 'ecommCheckout')) {
      return isCheckoutPage;
    }

    return handleEcommerceStandard();
  };

  // Handle eProcurement standard checks
  const handleEprocurementStandard = () => {
    if (
      isCartPage &&
      getCheckboxValue(elements, 'eprocCart') &&
      (includedPaths === '' ||
        includedPaths === undefined ||
        includedPaths?.includes(userDetails?.mailingAddressCountryCode.toLowerCase()) ||
        isPathIncluded(includedPaths)) // Pass true to indicate eProcurement
    )
      return true;
    if (!isCartPage && getCheckboxValue(elements, 'eproc')) return true;
    return false;
  };

  // Handle eProcurement site type logic
  const handleEprocurementSite = () => {
    if (checkAllFalse(elements)) {
      return isCartPage;
    }

    if (isOnlyCheckboxTrue(elements, 'eprocCart')) {
      return isCartPage;
    }

    return handleEprocurementStandard();
  };

  // Determine if banner should be shown based on site config
  const determineBannerVisibility = () => {
    if (isEmpty(elements)) return false;

    // Never show banner on Quick Order page
    if (isQuickOrderPage) return false;

    // First check if the current path is included in the includedPaths
    if (
      includedPaths &&
      includedPaths.trim() !== '' &&
      !isPathIncluded(includedPaths) &&
      siteConfig === 'eCommerce'
    ) {
      return false;
    }

    switch (siteConfig) {
      case 'eCommerce':
        return handleEcommerceSite();
      case 'eProcurement':
        return handleEprocurementSite();
      case 'microSite':
        return getCheckboxValue(elements, 'microsite');
      default:
        return false;
    }
  };

  useEffect(() => {
    setShowBanner(determineBannerVisibility());
  }, [announcementData, siteConfig, currentPath, userDetails]);

  if (!showBanner || isEmpty(announcementData)) {
    return null;
  }

  return (
    <div className="container">
      <div id="special-announcement-container">
        <div className="cmp-special-announcement-banner">
          <div className="container">
            <div className="cmp-special-announcement-content">
              {iconValue && (
                <div className="cmp-special-announcement-icon">
                  <ReactSVG src={iconValue} />
                </div>
              )}
              <div
                className="cmp-special-announcement-description"
                dangerouslySetInnerHTML={{ __html: descriptionValue }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SpecialAnnouncementBanner.propTypes = {
  announcementData: PropTypes.shape({
    entities: PropTypes.arrayOf(
      PropTypes.shape({
        properties: PropTypes.shape({
          elements: PropTypes.shape({
            description: PropTypes.shape({
              value: PropTypes.string,
            }),
            icon: PropTypes.shape({
              value: PropTypes.string,
            }),
            ecomm: PropTypes.shape({
              value: PropTypes.bool,
            }),
            eproc: PropTypes.shape({
              value: PropTypes.bool,
            }),
            microsite: PropTypes.shape({
              value: PropTypes.bool,
            }),
            ecommCart: PropTypes.shape({
              value: PropTypes.bool,
            }),
            ecommCheckout: PropTypes.shape({
              value: PropTypes.bool,
            }),
            eprocCart: PropTypes.shape({
              value: PropTypes.bool,
            }),
            includedPaths: PropTypes.shape({
              value: PropTypes.string,
            }),
          }),
        }),
      })
    ),
  }),
  siteConfig: PropTypes.string,
  userDetails: PropTypes.shape({}),
};

SpecialAnnouncementBanner.defaultProps = {
  announcementData: {},
  siteConfig: 'eCommerce',
  userDetails: {},
};

export default SpecialAnnouncementBanner;
